/**
 * LA SALUD DE UN PROYECTO EN 5 PREGUNTAS (PROYECTOS-ALINEADOS-T1; Josse 31-oct #39195, boceto P #39215, SI Ares #39216).
 *
 * La vista Proyectos mostraba numeros escritos a mano (version del 14-ago, "Documentacion 2 %" de una auditoria del
 * 2-jul) que chocaban con lo que mide la Sala. Aqui cada numero sale de lo medido y dice su fuente y su fecha:
 *   - lo servido: el sello vivo (SelloVivoClient); el escritorio dice "pendiente de la API" hasta que A lo publique;
 *   - capas, limpieza y contratos: GET /api/v1/salud/vigentes (la ultima foto de cada numero con dueno, fuente y
 *     vencida; vencida la calcula la base a los 7 dias, igual que la Sala);
 *   - el rumbo: las misiones vivas del proyecto y, cuando A lo parta por repo, el ambito trabajo con sujeto = repo.
 *
 * REGLAS (las cuida scripts/check-proyectos-alineados.mjs):
 *   - Un numero que no esta medido dice "sin medir", nunca 0 ni el numero anotado a mano.
 *   - Una foto vencida se muestra con la palabra vencida; no se esconde ni se pinta como de hoy.
 *   - Lo anotado a mano nunca ocupa el lugar de lo servido.
 *
 * Sin DOM.
 * @module application/proyectos/saludDelProyecto
 */

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

/** Una fila de salud/vigentes en una forma (la API manda camelCase; se tolera snake_case). */
export function leerFila(f) {
  const valor = pick(f, 'valor', 'Valor');
  return {
    ambito: txt(pick(f, 'ambito', 'Ambito')),
    clave: txt(pick(f, 'clave', 'Clave')),
    sujeto: txt(pick(f, 'sujeto', 'Sujeto')),
    fecha: txt(pick(f, 'fecha', 'Fecha')).slice(0, 10),
    valor: valor === null || valor === '' ? null : Number(valor),
    dueno: txt(pick(f, 'dueno', 'Dueno')),
    fuente: txt(pick(f, 'fuente', 'Fuente')),
    vencida: pick(f, 'vencida', 'Vencida') === true,
  };
}

function buscar(filas, clave, sujeto) {
  return filas.find((f) => f.clave === clave && f.sujeto === sujeto) || null;
}

/** El agente dueno del repo segun la foto de salud (el mismo que la base pone al publicar capas). null si no hay foto. */
export function duenoDelRepo(filas, projectKey) {
  const f = filas.find((x) => x.sujeto === projectKey && x.dueno);
  return f ? f.dueno : null;
}

/**
 * Los origenes con que ese dueno escribe en la bitacora. La API filtra por igualdad exacta y la flota no escribe igual:
 * el escritorio y la API publican en minusculas (argy-desktop-local-001) y el panel en mayusculas (medido 31-oct, 7 dias).
 */
export function origenesDelDueno(dueno) {
  const d = txt(dueno);
  if (!d) return [];
  return [...new Set([d, d.toLowerCase(), d.toUpperCase()])];
}

const fuenteDe = (f) => (f ? `${f.fuente || 'salud/vigentes'} · foto del ${f.fecha}` : '');
const estadoDe = (...fs) => {
  const presentes = fs.filter(Boolean);
  if (!presentes.length || presentes.every((f) => f.valor === null)) return 'sin-medir';
  return presentes.some((f) => f.vencida) ? 'vencida' : 'medido';
};
const fmtPct = (v) => `${Number(v).toLocaleString('es-CO', { maximumFractionDigits: 1 })} %`;

/** La primera pregunta: que se esta sirviendo. */
export function preguntaServida(projectKey, sello) {
  const base = { id: 'servida', pregunta: '¿Qué se está sirviendo?' };
  // El escritorio lo lee la API en el servidor (GET salud/escritorio-version, A #39229); mientras no conteste, pendiente.
  if (projectKey === 'nexo-desktop' && !(sello && sello.estado === 'servido')) {
    return { ...base, estado: 'pendiente', valor: 'pendiente de la API',
      detalle: 'el manifiesto publicado (releases/v1/Nexo CM.application) se lee en el servidor; el navegador no lo puede leer desde el panel',
      fuente: 'pedido a la API (Ares #39216)' };
  }
  if (sello && sello.estado === 'servido') {
    return { ...base, estado: 'medido', valor: `v${sello.version}`,
      detalle: sello.commit ? `commit ${String(sello.commit).slice(0, 7)}` : (sello.extra || ''),
      fuente: sello.fuente || 'sello vivo, leido ahora' };
  }
  return { ...base, estado: 'sin-medir', valor: 'sin medir',
    detalle: sello ? sello.porque : 'midiendo...', fuente: 'sello vivo' };
}

/**
 * Las 5 preguntas. `filas` son las de salud/vigentes (arquitectura + trabajo) ya pasadas por leerFila;
 * `misiones` las misiones vivas que la vista ya unio al proyecto.
 */
export function cincoPreguntas({ projectKey, filas = [], sello = null, misiones = [] }) {
  const c0 = buscar(filas, 'capa0_conocidos_pct', projectKey);
  const hu = buscar(filas, 'capa1_huerfanos', projectKey);
  const dor = buscar(filas, 'capa1_dormidos', projectKey);
  const bin = buscar(filas, 'capa1_binarios', projectKey);
  const con = buscar(filas, 'contratos_activos_pct', projectKey);
  const sinMisionRepo = buscar(filas, 'tareas_abiertas_sin_mision', projectKey);
  const sinMisionCasa = buscar(filas, 'tareas_abiertas_sin_mision', '_global');

  const limpio = [hu && `${hu.valor} huérfanos`, dor && `${dor.valor} dormidos`, bin && `${bin.valor} binarios`].filter(Boolean);
  const vivas = misiones.length;

  return [
    preguntaServida(projectKey, sello),
    { id: 'conocemos', pregunta: '¿Sabemos qué hay adentro?', estado: estadoDe(c0),
      valor: c0 && c0.valor !== null ? fmtPct(c0.valor) : 'sin medir',
      detalle: 'archivos de código que nombra un nodo del mapa (capa 0)', fuente: fuenteDe(c0) },
    { id: 'limpio', pregunta: '¿Está limpio?', estado: estadoDe(hu, dor, bin),
      valor: limpio.length ? limpio.join(' · ') : 'sin medir',
      detalle: 'código que nadie usa y archivos que no deberían vivir en el repo (capa 1)', fuente: fuenteDe(hu || dor || bin) },
    { id: 'contratos', pregunta: '¿Cada pieza dice qué promete?', estado: estadoDe(con),
      valor: con && con.valor !== null ? fmtPct(con.valor) : 'sin medir',
      detalle: 'nodos activos con su contrato (qué hace, para quién, cómo se prueba)', fuente: fuenteDe(con) },
    { id: 'rumbo', pregunta: '¿El trabajo tiene rumbo?', estado: sinMisionRepo ? estadoDe(sinMisionRepo) : 'medido',
      valor: `${vivas} ${vivas === 1 ? 'misión viva' : 'misiones vivas'}`,
      detalle: sinMisionRepo
        ? `${sinMisionRepo.valor} tareas abiertas sin misión en este repo`
        : `por repo: pendiente de la API${sinMisionCasa && sinMisionCasa.valor !== null ? `; en toda la casa, ${sinMisionCasa.valor} tareas abiertas sin misión` : ''}`,
      fuente: sinMisionRepo ? fuenteDe(sinMisionRepo) : (sinMisionCasa ? fuenteDe(sinMisionCasa) : 'misiones vivas del proyecto') },
  ];
}

/** Una linea para toda la casa: el respaldo de la base principal (nexo_db). */
export function lineaRespaldo(filasRespaldo = []) {
  const f = filasRespaldo.find((x) => x.clave === 'horas_desde_respaldo' && x.sujeto === 'nexo_db') || null;
  if (!f) return { estado: 'sin-medir', texto: 'Respaldo de la base: sin medir', fuente: 'salud/vigentes, ámbito respaldo' };
  if (f.valor === null) return { estado: 'sin-respaldo', texto: 'Respaldo de la base: no hay ningún respaldo medido', fuente: fuenteDe(f) };
  const h = Math.round(f.valor);
  return { estado: f.vencida ? 'vencida' : 'medido', texto: `Respaldo de la base: el último fue hace ${h} ${h === 1 ? 'hora' : 'horas'}`, fuente: fuenteDe(f) };
}

/** Los codigos de mision que nombran los eventos (notas.mission_code). Lo usa la pestana Misiones para unir por dueno. */
export function misionesDeLosEventos(eventos = []) {
  const codigos = new Set();
  for (const e of eventos) {
    let n = e && e.notas;
    if (typeof n === 'string') { try { n = JSON.parse(n); } catch { n = null; } }
    const c = n && txt(n.mission_code);
    if (c) codigos.add(c.toUpperCase());
  }
  return codigos;
}
